import { Icon } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import ButtonGroupDynamic from "../../common/ButtonGroupDynamic";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { RETAILER_RULE_SLUG } from "../rulesAndOutcomes/outcomes/retailerOutcome/retailerOutcomeUtils";
import { STORE_GROUP_RULE_SLUG } from "../rulesAndOutcomes/outcomes/storeOutcome/storeOutcomeUtils";
import OutcomeRuleSelectorModal from "./OutcomeRuleSelectorModal";
import "./activitySetup.scss";

const OutcomeRulesSetup = ({ outcome, updateOutcome }) => {
  const [showOutcomeRuleModal, setShowOutcomeRuleModal] = useState(false);

  // Ensure required rules exist for the provided outcome
  useEffect(() => {
    if (
      !outcome?.outcomeDefinition?.requiredRules ||
      !outcome?.outcomeDefinition?.rules
    )
      return;

    const existingRuleSlugs = (outcome.outcomeRules || []).map(
      (r) => r.ruleDefinition?.slug
    );

    const missingRequiredRules = outcome.outcomeDefinition.rules.filter(
      (rule) =>
        outcome.outcomeDefinition.requiredRules?.includes(rule.slug) &&
        !existingRuleSlugs.includes(rule.slug)
    );

    if (missingRequiredRules.length > 0) {
      const formattedRequired = missingRequiredRules.map((rule) => ({
        ruleDefinition: rule,
        ruleData: { ...presetJSONData(rule.inputSchema) },
        isRequired: true,
      }));
      const updated = structuredCloneSafe(outcome);
      updated.outcomeRules = [...(updated.outcomeRules || []), ...formattedRequired];
      updateOutcome(updated);
    }
  }, [outcome, updateOutcome]);

  const structuredCloneSafe = (obj) => {
    if (typeof structuredClone === "function") return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
  };

  const presetJSONData = (inputSchema) => {
    const data = {};
    if (inputSchema && Object.keys(inputSchema || {}).length) {
      const properties = inputSchema.properties || {};
      for (const key in properties) {
        if (
          properties[key]?.hidden === true &&
          properties[key]?.const !== undefined
        ) {
          data[key] = properties[key].const;
        }
      }
    }
    return data;
  };

  const updateOutcomeRule = (ruleData, ruleIndex) => {
    const updated = structuredCloneSafe(outcome);
    updated.outcomeRules[ruleIndex].ruleData = ruleData;
    updateOutcome(updated);
  };

  const addOutcomeRule = (newOutcomeRuleDefinition) => {
    const updated = structuredCloneSafe(outcome);
    const formattedOutcomeRule = {
      ruleDefinition: newOutcomeRuleDefinition,
      ruleData: { ...presetJSONData(newOutcomeRuleDefinition.inputSchema) },
    };
    updated.outcomeRules = [...(updated.outcomeRules || []), formattedOutcomeRule];
    updateOutcome(updated);
    setShowOutcomeRuleModal(false);
  };

  const removeOutcomeRule = (ruleIndex) => {
    const updated = structuredCloneSafe(outcome);
    updated.outcomeRules.splice(ruleIndex, 1);
    updateOutcome(updated);
  };

  const displayAddOutcomeRuleOption = (currentOutcome) => {
    if (
      currentOutcome?.outcomeDefinition?.slug === "allocate-sweepstakes-entries" ||
      currentOutcome?.outcomeDefinition?.slug === "instant-win-outcome"
    ) {
      return false;
    }

    const filterOutHiddenRules =
      currentOutcome.outcomeDefinition?.rules?.filter(
        (rule) => rule.hidden !== true
      ) || [];
    const totalAllowedCountOfRules =
      filterOutHiddenRules?.reduce((acc, rule) => acc + (rule.maxAllowed ?? 0), 0) ||
      0;

    const isAnyRuleHasmaxAllowedNull = filterOutHiddenRules?.some(
      (rule) => rule.maxAllowed === null
    );

    return (
      isAnyRuleHasmaxAllowedNull ||
      (currentOutcome.outcomeRules?.length || 0) < totalAllowedCountOfRules
    );
  };

  // Build a minimal activity stub to reuse OutcomeRuleSelectorModal without duplication
  const activityStub = {
    outcomes: [outcome],
  };

  return (
    <div className="mt-3 display-flex flex-direction-column outcome-rule-padding">
      {(outcome?.outcomeRules || []).map((outcomeRule, outcomeRuleIndex) => {
        if (outcomeRule.ruleDefinition?.hidden) {
          return null;
        }
        const slug = outcomeRule.ruleDefinition?.slug;
        if (slug === RETAILER_RULE_SLUG || slug === STORE_GROUP_RULE_SLUG) {
          return null;
        }
        return (
          <div className="outcome-form-container" key={outcomeRuleIndex}>
            <JsonFormsWrapper
              jsonschema={outcomeRule.ruleDefinition?.inputSchema || {}}
              uischema={outcomeRule.ruleDefinition?.uiSchema || {}}
              data={outcomeRule.ruleData || {}}
              onChange={(data) => updateOutcomeRule(data, outcomeRuleIndex)}
            />
            {!outcomeRule.isRequired && (
              <div className="ml-3">
                <ButtonGroupDynamic
                  className="icon-button-group"
                  items={[
                    {
                      icon: "delete",
                      onClick: () => removeOutcomeRule(outcomeRuleIndex),
                    },
                  ]}
                  size="small"
                />
              </div>
            )}
          </div>
        );
      })}

      {displayAddOutcomeRuleOption(outcome) && (
        <button
          style={{
            border: "1px solid #D7E5F8",
            borderRadius: "8px",
            borderStyle: "dashed",
            padding: "16px",
            background: "transparent",
            width: "100%",
          }}
          className="display-flex align-items-center cursor-pointer"
          onClick={() => setShowOutcomeRuleModal(true)}
          type="button"
        >
          <span
            className="button $font-xsmall-regular $spacing-p03"
            style={{
              background: "#F7FAFE",
              border: "1px solid #D7E5F8",
              borderRadius: "5px",
              color: "#005cbc",
            }}
          >
            <Icon icon="add" alt="Add" size="small" variant="primary" />
          </span>
          <p className="$font-xsmall-bold ml-10">Add new outcome rule</p>
        </button>
      )}

      {showOutcomeRuleModal && (
        <OutcomeRuleSelectorModal
          activity={activityStub}
          selectedOutcomeIndex={0}
          showOutcomeRuleModal={showOutcomeRuleModal}
          addOutcomeRule={addOutcomeRule}
          onCloseOutcomeRuleModal={() => setShowOutcomeRuleModal(false)}
        />
      )}
    </div>
  );
};

OutcomeRulesSetup.propTypes = {
  outcome: PropTypes.object.isRequired,
  updateOutcome: PropTypes.func.isRequired,
};

export default OutcomeRulesSetup;
